import { Draggable } from "@hello-pangea/dnd";
import DragIcon from "../icons/DragIcon";
import TrashIcon from "../icons/TrashIcon";
import { fetchModelByUrl } from "../../api/modelApi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";

/**
 * MenuItem component represents an individual item in the menu that can be edited and reordered.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {Object} props.item - The menu item data.
 * @param {string} props.item.id - The unique identifier for the menu item.
 * @param {string} props.item.name - The name of the menu item.
 * @param {string} props.item.price - The price of the menu item.
 * @param {string} props.item.description - The description of the menu item.
 * @param {string[]} props.item.dietaryLabels - The dietary labels of the menu item.
 * @param {Object} [props.item.model] - The associated model data (optional)
 * @param {number} props.index - The index of the menu item in the list.
 * @param {Object} props.handlerFunctions - The handler functions for updating and deleting the item.
 * @param {Function} props.handlerFunctions.onItemUpdate - Function to call when the item is updated.
 * @param {Function} props.handlerFunctions.onItemDelete - Function to call when the item is deleted.
 * @param {boolean} props.editMode - Flag indicating whether the menu is in edit mode.
 *
 * @returns {JSX.Element} The rendered MenuItem component.
 */
const MenuItem = ({ item, index, handlerFunctions, editMode }) => {
  let name = item.name;
  let price = item.price;
  let description = item.description;
  let dietaryLabels = item.dietaryLabels || [];

  const { onItemUpdate, onItemDelete } = handlerFunctions;

  const handleNameChange = (event) => {
    name = event.target.value;
    handleItemUpdate();
  };

  const handlePriceChange = (event) => {
    price = event.target.value;
    price = price.replace(/^\$/, "");
    handleItemUpdate();
  };

  const handleDescriptionChange = (event) => {
    description = event.target.value;
    handleItemUpdate();
  };

  const handleItemUpdate = () => {
    onItemUpdate({
      name,
      price,
      description,
      dietaryLabels,
      id: item.id,
    });
  };

  const handleDownload = async (event, modelUrl) => {
    event.preventDefault(); // Prevent default <a> behavior

    try {
      const blob = await fetchModelByUrl(modelUrl);
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = modelUrl.split("/").pop(); // Extract filename from URL
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const draggableId = `draggable-item-${item.id}`;

  return (
    <Draggable key={item.id} draggableId={draggableId} index={index}>
      {(provided) => (
        <div
          className={`menu-item ${!editMode && "view-mode"}`}
          {...provided.draggableProps}
          ref={provided.innerRef}
        >
          {editMode && (
            <button
              className="menu-section-button menu-section-drag"
              {...provided.dragHandleProps}
            >
              <DragIcon />
            </button>
          )}
          <div className="menu-item-text">
            <input
              className="menu-item-name menu-item-input-values"
              value={name}
              onFocus={(e) => e.target.select()}
              onChange={handleNameChange}
              disabled={!editMode}
            />
            <input
              className="menu-item-input-values menu-item-desc"
              value={description}
              onFocus={(e) => e.target.select()}
              onChange={handleDescriptionChange}
              disabled={!editMode}
            />
          </div>

          <div className="menu-item-back-items">
            <input
              className="menu-item-input-values menu-item-price"
              value={`$${price}`}
              onChange={handlePriceChange}
              onFocus={(e) => e.target.select()}
              disabled={!editMode}
            />
            {item.generateModel && item.modelStatus && editMode && (
              <span
                className={`status-badge ${item.modelStatus.toLowerCase()}`}
              >
                {item.modelStatus === "PENDING" && "generating..."}
                {item.modelStatus === "READY" && "ready"}
                {item.modelStatus === "FAILED" && "failed"}
              </span>
            )}
            <div className="menu-item-model">
              {item.model && editMode && (
                <div
                  className="menu-model-file-download-container"
                  onClick={(e) => handleDownload(e, item.model.url)}
                >
                  <FontAwesomeIcon
                    className="menu-download-icon"
                    icon={faDownload}
                    size="sm"
                  />
                </div>
              )}
            </div>
            {editMode && (
              <button
                className="menu-section-button menu-section-trash"
                onClick={() => onItemDelete(item)}
              >
                <TrashIcon />
              </button>
            )}
          </div>
        </div>
      )}
    </Draggable>
  );
};

export default MenuItem;
